"use client";

import { useTranslation } from "react-i18next";
import { pickLocale } from "./format";
import type { I18nText } from "./api";

/**
 * Locale-aware wrapper around `pickLocale` for client components.
 *
 * Reads the active i18next locale (which itself hydrates from the
 * `granbay.region` cookie via I18nProvider) and binds it as the
 * default. Components can call the returned function with just the
 * I18nText and get the right locale's text without threading the
 * locale through manually.
 *
 * 2026-05-08 — fixes the founder-reported bug where /markets/<slug>
 * rendered the English title for a viewer with the BR locale active.
 * Root cause was 20+ pickLocale call sites that all defaulted to "en"
 * because no caller passed the locale arg. This hook removes the
 * footgun for client components; server components can still call
 * `pickLocale(text, await getServerLocale())`.
 */
export function usePickLocale() {
  const { i18n } = useTranslation();
  const locale = i18n.language || "en";
  return (text: I18nText): string => pickLocale(text, locale);
}
